import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMap } from './map-view';

export interface HoverTarget {
  id: string;
  lngLat: [number, number];
}

/**
 * Small card anchored to a map position (e.g. the station under the pointer). It stays open while
 * the pointer moves from the feature onto the card, so its buttons can be clicked.
 */
export function useHoverCard() {
  const [target, setTarget] = useState<HoverTarget | null>(null);
  const [pinned, setPinned] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cancelHide = () => clearTimeout(hideTimer.current);
  const hideSoon = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => {
      setTarget(null);
      setPinned(false);
    }, 300);
  };
  useEffect(() => () => clearTimeout(hideTimer.current), []);
  return {
    target,
    pinned,
    /** Feature hover from a map layer (null when the pointer leaves it). */
    onFeatureHover(t: HoverTarget | null) {
      if (pinned) return;
      if (t) {
        cancelHide();
        setTarget(t);
      } else hideSoon();
    },
    /** Feature click: keep the card open until closed or another feature is clicked. */
    onFeatureClick(t: HoverTarget) {
      cancelHide();
      setTarget(t);
      setPinned(true);
    },
    close() {
      cancelHide();
      setTarget(null);
      setPinned(false);
    },
    cardEvents: { onMouseEnter: cancelHide, onMouseLeave: () => !pinned && hideSoon() },
  };
}

export function HoverCard({
  at,
  children,
  events,
}: {
  at: [number, number];
  children: ReactNode;
  events: { onMouseEnter: () => void; onMouseLeave: () => void };
}) {
  const map = useMap();
  const [, redraw] = useState(0);
  useEffect(() => {
    if (!map) return;
    const update = () => redraw((n) => n + 1);
    map.on('move', update);
    return () => {
      map.off('move', update);
    };
  }, [map]);
  if (!map) return null;
  const p = map.project(at);
  return (
    <div
      className="absolute z-20 -translate-x-1/2 pb-3"
      style={{ left: p.x, top: p.y, transform: 'translate(-50%, calc(-100% - 6px))' }}
      {...events}
    >
      <div className="min-w-48 rounded-lg bg-white p-3 text-sm shadow-lg ring-1 ring-slate-200">
        {children}
      </div>
    </div>
  );
}
